import ReportIcon from '@mui/icons-material/Report';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import {
    Breadcrumbs,
    Button,
    ClickAwayListener,
    Link,
    Stack,
    StepIconProps,
    TextField,
    Typography,
} from '@mui/material';
import Step from '@mui/material/Step';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import { styled } from '@mui/material/styles';
import { useOktaAuth } from '@okta/okta-react';
import { observer } from 'mobx-react-lite';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import Util from '../Util';
import Backend from '../backend/Backend';
import DateSelectionDialog from '../components/DateSelectionDialog';
import SITooltip from '../components/SITooltip';
import ValidationRectangle from '../components/ValidationRectangle';
import { ReactComponent as ConfigEditIcon } from '../resources/images/edit_config.svg';
import AquantWarningIcon from '../resources/images/process_step_warning.svg';
import stepSelectGoalActiveIcon from '../resources/images/step_select_goal_active.svg';
import stepSelectGoalCompleteIcon from '../resources/images/step_select_goal_complete.svg';
import stepSelectGoalNotCompleteIcon from '../resources/images/step_select_goal_not_complete.svg';
import { ConfigStore } from '../stores/ConfigStore';
import { WizardItem, WizardStore } from '../stores/WizardStore';
import { goals_list } from './Goals';
import StepConfirmation, { ConfirmationDialogProps, defaultConfirmationDialogProps } from './StepConfirmation';
import './Wizard.scss';

const CreateStepConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 22,
    },
    [`&.${stepConnectorClasses.active}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            backgroundImage:
                'linear-gradient(360deg, #3775FC -12.06%, rgba(55, 117, 252, 0.0001) 17.17%, rgba(55, 117, 252, 0.0001) 74.83%, #3775FC 111.38%)',
        },
    },
    [`&.${stepConnectorClasses.completed}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            border: '1px solid rgba(169, 221, 237, 1)',
        },
    },
    [`& .${stepConnectorClasses.line}`]: {
        border: '1px solid rgba(169, 221, 237, 1)',
    },
}));

const CreateStepIconRoot = styled('div')<{
    ownerState: { completed?: boolean; active?: boolean };
}>(({ theme, ownerState }) => ({
    zIndex: 1,
    width: 32,
    height: 32,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    ...(ownerState.active && {}),
    ...(ownerState.completed && {}),
}));

function CreateStepIcon(props: StepIconProps) {
    const { active, completed, className } = props;

    return (
        <CreateStepIconRoot ownerState={{ completed, active }} className={className}>
            {active ? (
                <img alt="" src={stepSelectGoalActiveIcon} />
            ) : completed ? (
                <img alt="" src={stepSelectGoalCompleteIcon} />
            ) : (
                <img alt="" src={stepSelectGoalNotCompleteIcon} />
            )}
        </CreateStepIconRoot>
    );
}

const getSaveConfirmProps = (saveConfig: () => void, skipConfirm: () => void) => {
    const res: ConfirmationDialogProps[] = [];

    const saveConfirmProps: ConfirmationDialogProps = {
        ...defaultConfirmationDialogProps,
        title: 'Are you sure?',
        content: (
            <>
                <div style={{ display: 'flex', marginBottom: 15 }}>
                    <img src={AquantWarningIcon} alt="warning-icon" style={{ marginRight: 5 }} />
                    <Typography variant="subtitle2">Changes to the configuration haven't been saved</Typography>
                </div>
                <Typography variant="body1">
                    To automatocally retreive these last settings, save your changes
                </Typography>
            </>
        ),
        onYes: saveConfig,
        yesLabel: 'Save',
        onClose: skipConfirm,
        closeLabel: 'Skip',
    };

    res.push(saveConfirmProps);

    return res;
};

interface WizardProps extends RouteComponentProps {
    progress: Function;
    configStore: ConfigStore;
    wizardStore: WizardStore;
    isEdit: boolean;
}

const ConfigNameTextField = styled(TextField)({
    borderBottom: '1px solid #DADCDF',
    width: '230px',
});

const CreateHeader = (props: {
    configName: string;
    setConfiguratioName: Function;
    currentStepTitle: string;
    saveConfig?: any;
    isSaveBtnDisabled?: boolean;
    isConfigSavedSuccessfully?: boolean;
    configStore: ConfigStore;
}) => {
    const { t } = useTranslation();

    const { configName, setConfiguratioName, currentStepTitle, saveConfig } = props;
    const [configNameEditModeOn, toggleConfigNameEditMode] = useState<boolean>(false);
    const [tempConfigName, setTempConfigName] = useState<string>('');
    const currentTenant = useRef(
        Util.getSessionCurrentTenantInfo() && Util.getSessionCurrentTenantInfo().id
            ? Util.getSessionCurrentTenantInfo()
            : Util.getCurrentTenantInfo(),
    );

    useEffect(() => {
        if (!configName || configName.length === 0) {
            const newName = Util.generateNewConfigName();
            setTempConfigName(newName);
            setConfiguratioName(newName);
        } else {
            setTempConfigName(configName);
        }
    }, [setConfiguratioName, configName]);

    const renderLogoAndConfigName = () => {
        const handleApprove = () => {
            setConfiguratioName(tempConfigName);
            toggleConfigNameEditMode(false);
        };

        const handleCancel = () => {
            setTempConfigName(configName);
            toggleConfigNameEditMode(false);
        };

        return (
            <Breadcrumbs className="Breadcrumbs">
                {currentTenant.current.logo !== 'null' ? (
                    <img src={currentTenant.current.logo} className="BreadcrumbsLogo" alt="tenant-logo" />
                ) : (
                    <Typography className="BreadcrumbsTypo" component="div">
                        {currentTenant.current.name}
                    </Typography>
                )}
                <Stack direction="row">
                    {configNameEditModeOn ? (
                        <ClickAwayListener onClickAway={() => configNameEditModeOn && handleCancel()}>
                            <div className="ConfigNameEdit">
                                <ConfigNameTextField
                                    size="small"
                                    placeholder={t('wizard.data-source.config-name')}
                                    onChange={(e) => setTempConfigName(e.target.value)}
                                    value={tempConfigName}
                                    variant="standard"
                                    InputProps={{
                                        disableUnderline: true,
                                        classes: {
                                            input:
                                                tempConfigName.length > 0
                                                    ? 'ConfigNameTextField'
                                                    : 'ConfigNameTextFieldPlaceholder',
                                        },
                                    }}
                                />
                                <ValidationRectangle
                                    onApprove={() => handleApprove()}
                                    onCancel={() => handleCancel()}
                                    disabled={tempConfigName.length === 0}
                                />
                            </div>
                        </ClickAwayListener>
                    ) : (
                        <>
                            <Typography
                                data-testid="projectName"
                                className="BreadcrumbsTypo"
                                component="div"
                                onClick={() => toggleConfigNameEditMode(true)}
                            >
                                {configName}
                            </Typography>
                            <ConfigEditIcon
                                data-testid="wizard-edit-config-name-btn"
                                onClick={() => {
                                    toggleConfigNameEditMode(true);
                                }}
                                color="#2468FF"
                                style={{ cursor: 'pointer' }}
                            />
                        </>
                    )}
                </Stack>
            </Breadcrumbs>
        );
    };

    return (
        <Stack direction="row" className="Header">
            {saveConfig ? (
                <Stack alignItems="center" direction="row">
                    <Typography component="div" className="StepTitle">
                        {currentStepTitle}
                    </Typography>
                    <SITooltip
                        title={
                            props.isSaveBtnDisabled
                                ? 'Unable to save template due to a syntax error. Correct the syntax error in the highlighted line.'
                                : ''
                        }
                    >
                        <span>
                            <Button
                                disabled={props.isSaveBtnDisabled}
                                className="HeaderSaveBtn"
                                onClick={async () => {
                                    await saveConfig();
                                }}
                            >
                                Save
                            </Button>
                        </span>
                    </SITooltip>
                    {props.isConfigSavedSuccessfully !== null && (
                        <Stack>
                            {props.isConfigSavedSuccessfully ? (
                                <Stack direction="row" className="ConfigSavedfeedbackStack ConfigSavedSuccessfully">
                                    <TaskAltIcon className="ConfigSavedFeedbackIcon" />
                                    <Typography className="ConfigSavedFeedbackFont">Successfully saved!</Typography>
                                </Stack>
                            ) : null}
                            {!props.isConfigSavedSuccessfully && (
                                <Stack direction="row" className="ConfigSavedfeedbackStack ConfigSaveFailed">
                                    <ReportIcon className="ConfigSavedFeedbackIcon" />
                                    <Typography className="ConfigSavedFeedbackFont">
                                        We encountered an error while attempting to save the template. Please try again.
                                    </Typography>
                                </Stack>
                            )}
                        </Stack>
                    )}
                </Stack>
            ) : null}
            {renderLogoAndConfigName()}
        </Stack>
    );
};

const Wizard: FunctionComponent<WizardProps> = observer((props: WizardProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { authState, oktaAuth } = useOktaAuth();
    const { configStore } = props;
    const { wizardStore } = props;

    // Project level
    const { selected_goal, setSelectedGoal } = configStore;
    const { config_name, setConfigName } = configStore;
    const { parent_config_id, setParentConfigId } = configStore;
    const { data_processing_config_id, setDataProcessingConfigId } = configStore;
    const { createConfig, handleRunTemplateTest, saveDataSource } = configStore;
    const [isConfigSavedSuccessfully, setIsConfigSavedSuccessfully] = useState<boolean | null>(null);

    // Data Source
    const { lookupConfig, setLookupConfig } = configStore;

    // Data Settings
    const { dataSettings, getDataSettings } = configStore;
    const { template_json, setTemplateJson } = configStore;
    const { template_config, setTemplateConfig } = configStore;

    // Summary
    const { startDate, setStartDate } = configStore;
    const { endDate, setEndDate } = configStore;

    // Wizard
    const { wizardSteps } = wizardStore;
    const { activeStep, setActiveStep } = wizardStore;
    const [stepConfirmation, setStepConfirmation] = useState(false);
    const [confirmationDialogs, setConfirmationDialogs] = useState<ConfirmationDialogProps[]>();
    const { getMultipleAssignments } = wizardStore;
    const { loadedConfigMultipleAssignments, setLoadedConfigMultipleAssignments } = wizardStore;
    const { processDialogOpen, setProcessDialogOpen } = configStore;
    const { openDateSelection, setOpenDateSelection } = wizardStore;
    const processButton = useRef<HTMLButtonElement>();
    const { templateTestRun, setTemplateTestRun } = configStore;

    const handleOnSubmitDatesDialog = async (startDate: Date, endDate: Date) => {
        setOpenDateSelection(false);
        setStartDate(startDate);
        setEndDate(endDate);

        if (templateTestRun) {
            props.progress(true);
            await handleRunTemplateTest(
                oktaAuth,
                () => {
                    setActiveStep(activeStep + 1);
                    props.progress(false);
                },
                () => props.progress(false),
            );
        } else {
            setProcessDialogOpen(true, () => setActiveStep(activeStep + 1));
        }
    };

    useEffect(() => {
        if (isConfigSavedSuccessfully !== null) {
            setTimeout(() => {
                setIsConfigSavedSuccessfully(null);
            }, 5000);
        }
    }, [isConfigSavedSuccessfully]);

    useEffect(() => {
        if (!authState) {
            return;
        }

        if (props.isEdit && parent_config_id === '' && data_processing_config_id === '') {
            return;
        }

        if (!authState.isAuthenticated) {
            setTemplateJson(null);
            setTemplateConfig(null);
        } else if (!props.isEdit) {
            props.progress(true);
            Backend.getConfig(oktaAuth, parent_config_id).then((config) => {
                const { data } = config;
                if (data) {
                    const config = data;
                    let position = 0;
                    for (let i = 0; i < config.environments.length; i++) {
                        if (config.environments[i]?.template?.mapping_raw_json) {
                            position = i;
                            break;
                        }
                    }
                    const [targetObj] = config.environments.splice(position, 1);
                    config.environments.unshift(targetObj);
                    if (lookupConfig) {
                        const mappingRawJson = JSON.parse(config.environments[0].template.mapping_raw_json);
                        mappingRawJson.lookupObjects = lookupConfig;
                        config.environments[0].template.mapping_raw_json = JSON.stringify(mappingRawJson, null, '\t');
                        config.environments[0].template.mapping = mappingRawJson;
                    }
                    setTemplateJson(config.environments[0].template.mapping_raw_json);
                    setTemplateConfig(config);
                    props.progress(false);
                }
                props.progress(false);
            });
        }

        if (!dataSettings) {
            (async function () {
                props.progress(true);
                await getDataSettings(oktaAuth, () => {
                    props.progress(false);
                    setLoadedConfigMultipleAssignments(getMultipleAssignments());
                });
            })();
        }

        if (!selected_goal) {
            setSelectedGoal(goals_list[0]);
        }
    }, [parent_config_id, data_processing_config_id, props.isEdit]);

    const canAdvance = (isSaveForLater: boolean) => {
        const wizardActiveStep: WizardItem = wizardSteps.find((step: WizardItem, index) => index === activeStep);

        return wizardActiveStep.canAdvance ? wizardActiveStep.canAdvance(isSaveForLater, !!props.isEdit) : true;
    };

    const create_config_and_save = async (successCallback?: Function, failCallback?: Function) => {
        await createConfig(oktaAuth, successCallback, failCallback);
        await saveDataSource(oktaAuth);
    };

    const handleNext = async (saveForLater?: boolean) => {
        if (saveForLater) {
            props.progress(true);
            await create_config_and_save();
            props.progress(false);
            history.push('/');
            configStore.resetConfigData();
            return;
        }

        const wizardActiveStep: WizardItem = wizardSteps.find((step: WizardItem, index) => index === activeStep);

        const stepConfirmationProps = wizardActiveStep.getStepConfirmationProps
            ? wizardActiveStep.getStepConfirmationProps(wizardActiveStep.onNext)
            : undefined;

        setConfirmationDialogs(stepConfirmationProps);

        if (stepConfirmationProps && stepConfirmationProps.length > 0) {
            setStepConfirmation(true);
            return;
        }

        if (wizardActiveStep.onNext) {
            wizardActiveStep.onNext();
        }

        if (activeStep === wizardSteps.length - 1) {
            history.push('/configurations'); // user can close the window at this point
        }
    };

    const handleBack = () => {
        if (activeStep === 0 || props.isEdit) {
            const stepConfirmationProps = getSaveConfirmProps(
                async () => {
                    handleNext(true);
                },
                () => {
                    history.push('/');
                    configStore.resetConfigData();
                },
            );

            setConfirmationDialogs(stepConfirmationProps);

            if (
                activeStep === 0 &&
                wizardSteps[0].showSaveConfirmDialog &&
                wizardSteps[0].showSaveConfirmDialog(props.isEdit)
            ) {
                configStore.resetConfigData();
                history.push('/');
                return;
            }

            if (stepConfirmationProps.length > 0) {
                setStepConfirmation(true);
            }
        } else {
            setActiveStep(activeStep - 1);
        }
    };

    const renderStepper = () => (
        <Stepper className="Stepper" activeStep={activeStep} connector={<CreateStepConnector />}>
            {wizardSteps.map((step, index) => (
                <Step key={index}>
                    <div>
                        <StepLabel
                            classes={
                                index === activeStep
                                    ? { label: 'ActiveStep' }
                                    : index < activeStep
                                    ? { label: 'CompletedStep' }
                                    : { label: 'NotCompletedStep' }
                            }
                            StepIconComponent={CreateStepIcon}
                        >
                            {step.label}
                        </StepLabel>
                    </div>
                </Step>
            ))}
        </Stepper>
    );

    const renderHeader = () => (
        <CreateHeader
            configStore={configStore}
            isConfigSavedSuccessfully={isConfigSavedSuccessfully}
            isSaveBtnDisabled={
                wizardSteps[activeStep].isSaveBtnDisabled ? wizardSteps[activeStep].isSaveBtnDisabled() : false
            }
            saveConfig={
                wizardSteps[activeStep].hasSaveHeaderBtn
                    ? async () => {
                          props.progress(true);
                          await create_config_and_save(
                              () => {
                                  setIsConfigSavedSuccessfully(true);
                              },
                              () => {
                                  setIsConfigSavedSuccessfully(false);
                              },
                          );
                          props.progress(false);
                      }
                    : null
            }
            configName={config_name}
            setConfiguratioName={setConfigName}
            currentStepTitle={wizardSteps[activeStep].headerTitle()}
        />
    );

    const renderStepContent = () => wizardSteps.map((step, index) => index === activeStep && step.content());

    const renderNavActions = () => (
        <Stack
            className="NavActions"
            direction="row"
            justifyContent={activeStep === wizardSteps.length - 1 ? 'center' : 'space-between'}
            alignItems="center"
            spacing={2}
        >
            {activeStep !== wizardSteps.length - 1 && (
                <Button className="Back" onClick={handleBack} data-testid="wizard-back-btn">{`< ${t(
                    'common.back',
                )}`}</Button>
            )}
            <Stack direction="row">
                {wizardSteps.find((step, index) => index === activeStep).hasSaveForLater &&
                (canAdvance(true) || props.isEdit) ? (
                    <Link
                        sx={{ textDecoration: 'none' }}
                        className="SaveForLater"
                        data-testid="wizard-save-for-later-btn"
                        onClick={() => {
                            handleNext(true);
                        }}
                    >
                        {t('common.save-for-later')}
                    </Link>
                ) : null}
                <Button
                    disabled={!canAdvance(false)}
                    data-testid="wizard-next-btn"
                    className="Next"
                    ref={processButton}
                    variant="contained"
                    disableRipple
                    onClick={() => {
                        handleNext(false);
                    }}
                >
                    {wizardSteps.find((step, index) => index === activeStep).nextButtonLabel}
                </Button>
            </Stack>
        </Stack>
    );

    const renderStepConfirmation = () =>
        stepConfirmation &&
        confirmationDialogs && (
            <StepConfirmation
                confirmationDialogs={confirmationDialogs}
                finish={() => {
                    setStepConfirmation(false);
                }}
                cancel={() => setStepConfirmation(false)}
            />
        );

    const renderDateSelection = () => (
        <DateSelectionDialog
            config={template_config}
            open={openDateSelection}
            onClose={() => {
                setOpenDateSelection(false);
            }}
            onSubmit={(startDate: any, endDate: any) => {
                handleOnSubmitDatesDialog(startDate, endDate);
            }}
            actionTitle={templateTestRun ? 'Test template' : 'Process data'}
            actionDescription={templateTestRun ? 'template test' : 'data process'}
        />
    );

    return (
        <>
            {wizardSteps ? (
                <Stack className="Wizard" spacing={2} direction="column" sx={{ margin: 0, paddingBottom: '40px' }}>
                    {props.isEdit ? <Stack marginTop="16px" sx={{ backgroundColor: '#F0F2F5' }} /> : null}
                    {!props.isEdit && <div className="StepperRoot">{renderStepper()}</div>}

                    {renderHeader()}
                    {renderStepContent()}
                    {renderNavActions()}
                    {renderStepConfirmation()}
                </Stack>
            ) : null}

            {renderDateSelection()}
        </>
    );
});

export default Wizard;
